import type { ListBuilderList } from '../../../types';
import {
	accountColumn,
	boardActionColumn,
	boardActionDatesColumn,
	licenseColumn,
	reviewActionColumn,
	salesColumn,
	whyHereColumn,
} from '../columns';
import { hideOtherCardFilters, ownAccountsScopeRule } from '../filter-rules';
import { COMPLIANCE_ATTENTION_LIST_IDS } from '../list-ids';

/**
 * Adverse Action (PD-16252) — active buyers holding a license the state board
 * has a disciplinary action on record against, where the license is STILL IN
 * FORCE, so the account can keep ordering on it today.
 *
 * NOT the Dead License list, and deliberately a separate one (Chris,
 * 2026-09-20):
 *   Dead License   — the state will not let them use the license at all. The
 *                    fix is to replace it or stop selling.
 *   Adverse Action — the board acted and the license SURVIVED. There is no
 *                    system fix; the question is whether we keep selling, and
 *                    that is a judgement a person makes with the board record
 *                    in front of them.
 *
 * So the row carries ONE action — Review — which opens the review modal. A
 * row on this list means "a review is needed", never "here is the answer".
 *
 * COUNT PARITY. The locked `licenseAdverseInForce` filter is the same
 * `licenseDisciplinaryInForceSql` fragment the dashboard's Adverse Action card
 * counts with, and the locked active-buyer filter is the same population, so
 * the card's license figure and this list's row count are one predicate.
 * Reviews already recorded against the board record as it stands are excluded
 * from BOTH (see `_queryAdverseActionNet` in compliance-overview.service.ts
 * and `licenseExcludeIds` in licenses.service.ts) — a suppressed item must not
 * be counted on a card that opens a list it is missing from.
 */
export const adverseActionList: ListBuilderList = {
	id: COMPLIANCE_ATTENTION_LIST_IDS.adverseAction,
	cacheTTL: 300000,
	// Tab label, deliberately short: the tabs must fit the Needs Attention
	// strip at 1440px before the More dropdown swallows them (Chris,
	// 2026-09-20). The full name is above and on the dashboard card.
	name: 'Adverse',
	type: 'default',
	active: true,
	// Directly after Dead License, so the tab order mirrors the dashboard's
	// card order — the two are read as a pair.
	sortOrder: 2,
	filterRules: {
		...hideOtherCardFilters(['licenseAdverseInForce']),
		licenseSalesRep: ownAccountsScopeRule,
		licenseActiveBuyer: {
			defaultValue: [{ id: 'yes', label: 'Buying (sale in 90 days)' }],
			locked: true,
		},
		licenseAdverseInForce: {
			defaultValue: [
				{ id: 'yes', label: 'Board acted, license still in force' },
			],
			locked: true,
		},
	},
	columns: [
		accountColumn,
		salesColumn,
		licenseColumn,
		boardActionColumn,
		boardActionDatesColumn,
		whyHereColumn,
		reviewActionColumn,
	],
	defaultSortId: 'sales-90-desc',
};
